import { Button } from "@/components/ui/button";
import { AccreditationIndividual, TicketIssue } from "@/models/accreditation";
import { DashboardOrder, OrderStatus } from "@/models/Dashboard";
import { formatNumberToMoney } from "@/utils/cart";
import {
  dateToPortalDisplay,
  formatDateWithSmallMonth,
  formatDateWithTime,
} from "@/utils/date";
import { getBaseEventUrl } from "@/utils/url";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { toSentenceCase } from "js-convert-case";
import { Copy, EllipsisVerticalIcon, QrCode, XIcon } from "lucide-react";
import { Fragment, useState, useEffect } from "react";
import QRCode from "react-qr-code";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { set } from "lodash";
import { BASE_URL } from "@/dashboardQueries";
import { SendEmailFromTemplateModal } from "../SendEmailFromTemplateModal";
import { EVENT_KEY, ORGANISATION_KEY } from "@/pages/protected-route";
import toast from "react-hot-toast";
import { FormikValues } from "formik";

interface Props {
  individual: AccreditationIndividual;
  order: DashboardOrder;
  tickets: Array<TicketIssue>;
}

export const PaymentLinkDetail = ({ individual, order, tickets }: Props) => {
  // --- All hooks must be called unconditionally and before any early return ---
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sendEmailFromEmailTemplateOpen, setSendEmailFromEmailTemplateOpen] =
    useState(false);
  const [copyButtonText, setCopyButtonText] = useState<string | null>(null);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);

  const {
    data: orderData,
    isLoading,
    isError,
  } = useQuery(
    ["orderDetails", order],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/dashboard/orders/${order}?allOrders=true`
      );
      return response.data;
    },
    {
      enabled: !!order, // Only fetch if orderId exists
      retry: false, // Disable retries for 404 errors
      onError: (error) => {
        // if (axios.isAxiosError(error) && error.response?.status === 404) {
        //   navigate("/dashboard/orders"); // Redirect if 404
        // }
      },
    }
  );

  // --- Do NOT return before all hooks are called! ---
  // Move these returns below all hooks

  const deleteMutation = useMutation(
    () =>
      axios.delete(
        `${BASE_URL}/accreditation/individuals/orders/${orderData.id}`
      ),
    {
      onSuccess: () => {
        console.log("Order deactivated successfully!");
        queryClient.invalidateQueries(["individual", individual.id.toString()]); // Adjust the query key as needed
      },
      onError: (error) => {
        console.error("Error deactivating order:", error);
      },
    }
  );

  const sendEmailMutation = useMutation(
    (formData: FormikValues) =>
      axios.put(
        `${BASE_URL}/accreditation/individuals/orders/${orderData.id}/email`,
        formData
      ),
    {
      onSuccess: () => {
        toast.success("Email sent successfully!");
        queryClient.invalidateQueries(["individual", individual.id.toString()]);
      },
      onError: (error) => {
        console.error("Error sending email:", error);
      },
    }
  );

  const handleSendEmail = (values: FormikValues) => {
    sendEmailMutation.mutate(values);
    setSendEmailFromEmailTemplateOpen(false); // Close modal after submission
  };

  const event = JSON.parse(localStorage.getItem(EVENT_KEY) || "{}");
  const organisation = JSON.parse(
    localStorage.getItem(ORGANISATION_KEY) || "{}"
  );
  const paymentLink = `${
    import.meta.env.VITE_ENVIRONMENT == "local" ? "http" : "https"
  }://${organisation.domain}/${orderData?.uuid}/pay`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(paymentLink).then(() => {
      setCopyButtonText("Copied! 🎉");
      setTimeout(() => {
        setCopyButtonText(null);
      }, 2000);
    });
  };

  const ticketsWithOrders = tickets
    .filter((ticket) => ticket.order)
    .map((ticket) => ticket.allocation.ticket);

  // --- Only return after all hooks are called ---
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading order details.</div>;
  }

  return (
    <div className="w-full p-6 space-y-6">
      <SendEmailFromTemplateModal
        to={individual.email}
        subject={`Request of Payment for ${event.name}`}
        open={sendEmailFromEmailTemplateOpen}
        setOpen={setSendEmailFromEmailTemplateOpen}
        variables={{
          "{{payment_link}}": paymentLink,
          "{{first_name}}": individual.firstName,
          "{{last_name}}": individual.lastName,
          "{{email}}": individual.email,
          "{{event_name}}": event.name,
        }}
        onSubmit={handleSendEmail} // Pass the mutation handler
      />

      <Transition.Root show={deleteModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          // initialFocus={cancelButtonRef}
          onClose={setDeleteModalOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-dark-secondary dark:bg-opacity-80 backdrop-blur-lg bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex items-end justify-center p-4 text-center h-full items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white dark:bg-dark-primary px-4 pb-4 pt-4 text-left shadow-xl transition-all sm:my-8 sm:w-full  sm:max-w-lg sm:p-6 ">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                      >
                        {orderData.status === OrderStatus.PENDING
                          ? "Deactivate Payment Link"
                          : "Delete Payment and Tickets"}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {orderData.status === OrderStatus.PENDING ? (
                            <>
                              Are you sure you want to deactivate this payment
                              link? Any attached orders will be deleted and
                              these tickets will be unassigned from this
                              individual.
                            </>
                          ) : (
                            <>
                              Are you sure you want to delete this payment? It
                              is has been succesfully paid. Deleting it will
                              delete the payment and also the tickets associated
                              with it.
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                    <button
                      id="confirm-delete"
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                      onClick={() => {
                        deleteMutation.mutate();
                        setDeleteModalOpen(false);
                      }}
                    >
                      Yes,{" "}
                      {orderData.status === OrderStatus.PENDING
                        ? "Deactivate"
                        : "Delete"}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteModalOpen(false)}
                      // ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={qrCodeModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          // initialFocus={cancelButtonRef}
          onClose={setQrCodeModalOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-dark-secondary dark:bg-opacity-80 backdrop-blur-lg bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex items-end justify-center p-4 text-center h-full items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white dark:bg-dark-primary px-4 pb-4 pt-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6 ">
                  <div>
                    <div
                      className="absolute right-0 mr-4 -mt-2 cursor-pointer"
                      onClick={() => setQrCodeModalOpen(false)}
                    >
                      <XIcon className="w-6 h-6 text-zinc-500" />
                    </div>
                    <div className="flex items-center justify-center p-4 text-white">
                      <div className="p-3 rounded-md bg-zinc-800">
                        <QRCode
                          className="w-32 h-32"
                          value={paymentLink}
                          bgColor={"transparent"}
                          fgColor={"white"}
                        />
                        <div className="mt-3 text-center">Scan to pay</div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <div className="flex items-center justify-between -mb-4">
        <h2 className="text-xl font-semibold dark:text-white">Payment Link</h2>
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button className="flex items-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100">
              <span className="sr-only">Open options</span>
              <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
            </Menu.Button>
          </div>

          <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 dark:divide-white/10 dark:bg-zinc-800 dark:ring-white/10 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in">
            <div className="py-1">
              <Menu.Item>
                <button
                  onClick={() => setSendEmailFromEmailTemplateOpen(true)}
                  className="group dark:text-zinc-300 flex items-center px-4 py-2 text-sm text-gray-700 hover:text-zinc-500"
                >
                  Email
                </button>
              </Menu.Item>
              <Menu.Item>
                <button
                  onClick={() => setDeleteModalOpen(true)}
                  className="group dark:text-zinc-300 flex items-center px-4 py-2 text-sm text-gray-700 hover:text-zinc-500"
                >
                  {orderData.status === OrderStatus.PENDING
                    ? "Deactivate"
                    : "Delete"}
                </button>
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
      </div>

      {orderData.status === OrderStatus.PENDING && (
        <h4 className="text-zinc-400 text-sm">
          Copy and share to accept payments for these tickets with this link.
        </h4>
      )}
      {orderData.status === OrderStatus.PENDING && (
        <div className="text-white flex -mt-1">
          <div className="w-1/2 min-w-1/2">
            <div className="flex rounded-md shadow-sm flex-1">
              <input
                id="paymentLink"
                name="paymentLink"
                type="text"
                value={paymentLink}
                disabled
                placeholder="www.example.com"
                className="block min-w-1/3 flex-1 rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-white/5 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-zinc-800 bg-gray-50 dark:text-gray-400 text-gray-900"
              />
            </div>
          </div>
          <div className="flex flex-1 pl-2">
            <button
              type="button"
              onClick={handleCopyLink}
              className="rounded-md dark:bg-white/10 bg-black hover:bg-black/75 px-3 py-2 text-sm font-semibold text-white shadow-sm dark:hover:bg-white/20 flex items-center"
            >
              {copyButtonText ? copyButtonText : <Copy className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={() => setQrCodeModalOpen(true)}
              className="rounded-md dark:bg-white/10 bg-black hover:bg-black/75 px-3 py-2 text-sm font-semibold text-white shadow-sm dark:hover:bg-white/20 flex items-center ml-2"
            >
              <QrCode className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
      <div
        className={`grid grid-cols-2 gap-y-4 text-sm text-muted-foreground ${
          orderData.status === OrderStatus.SUCCESS ? "pt-2" : ""
        }`}
      >
        <div>Tickets</div>
        <div
          className="text-right font-medium text-foreground dark:text-white"
          id="payment-link-tickets"
        >
          {ticketsWithOrders.map((ticket, index) => (
            <span key={index}>
              {ticket.name}
              {index < ticketsWithOrders.length - 1 && ", "}
            </span>
          ))}
        </div>
        <div>Amount</div>
        <div className="text-right font-medium text-foreground dark:text-white">
          {formatNumberToMoney(orderData.total)}
        </div>
        <div>Fees</div>
        <div className="text-right font-medium text-foreground dark:text-white">
          {/* modify for EP */}
          €3.00{" "}
          <span className="dark:text-zinc-500 font-normal">
            (excl payment processor)
          </span>
        </div>
        <div>Status</div>
        <div className="text-right">
          {orderData.status === OrderStatus.PENDING ? (
            <div
              id="payment-link-status"
              className="inline-flex items-center rounded-md bg-blue-400/10 px-2 py-1 text-xs font-medium text-blue-400 ring-1 ring-inset ring-blue-400/30"
            >
              {toSentenceCase(orderData.status)}
            </div>
          ) : (
            <div
              id="payment-link-status"
              className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-400/30"
            >
              {toSentenceCase(orderData.status)}
            </div>
          )}
        </div>
        <div>Created</div>
        <div className="text-right dark:text-white">
          {formatDateWithTime(orderData.createdAt)}
        </div>
        {orderData.cardLast4 && (
          <>
            <div>Payment Method</div>
            <div className="text-right dark:text-white" id="card-last-4">
              Card •••• {orderData.cardLast4}
            </div>
          </>
        )}
        {/* Or N/A if unpaid */}
      </div>
      <div className="border-t dark:border-white/5 pt-4"></div>
      <div className="flex items-center justify-between text-base font-semibold dark:text-white">
        <div>Total</div>
        <div>{formatNumberToMoney(orderData.total)}</div>
      </div>
    </div>
  );
};
